<template>
  <div class="task-status-badge-wrapper">
    <div class="task-status-badge" :class="badgeClasses">
      {{ label }}
    </div>
    <div v-if="featureEnabled && dateLine" class="task-status-badge__date">
      {{ dateLine }}
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { LD_FLAG_IDEA_MDP_461 } from "@components/constants/launchDarklyFlagKeys";
import { formatStatusDate } from "@components/utils/formatStatusDate";

const props = defineProps({
  taskStatus: {
    type: String,
    default: "to_do",
    validator(value) {
      const normalized = value?.toLowerCase().replace(/\s+/g, "_") || "";
      return ["to_do", "in_progress", "planned", "completed", "canceled"].includes(normalized);
    },
  },
  date: {
    type: [String, Number, Date],
    default: null,
  },
});

const proxy = getCurrentInstance()?.proxy;
const featureEnabled = ref(true);

const normalizedStatus = computed(() => {
  return props.taskStatus?.toLowerCase().replace(/\s+/g, "_") || "";
});

const label = computed(() => {
  switch (normalizedStatus.value) {
    case "to_do":
      return "To Do";
    case "in_progress":
      return "In Progress";
    case "planned":
      return "Planned";
    case "completed":
      return "Completed";
    case "canceled":
      return "Canceled";
    default:
      return props.taskStatus;
  }
});

const badgeClasses = computed(() => {
  const statusClass = normalizedStatus.value.replace(/_/g, "-");
  return {
    [`task-status-badge--${statusClass}`]: true,
  };
});

const datePrefix = computed(() => {
  switch (normalizedStatus.value) {
    case "to_do":
    case "in_progress":
      return "By";
    case "completed":
    case "canceled":
      return "On";
    default:
      return "";
  }
});

const dateLine = computed(() => {
  if (!datePrefix.value) return "";
  const formatted = formatStatusDate(props.date);
  return formatted ? `${datePrefix.value} ${formatted}` : "";
});

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_461, true)) !== false;
  } catch {
    // Kill-switch lookup failures never hide the date line.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.task-status-badge-wrapper {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  max-width: 100%;
  min-width: 0;
}

.task-status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 12px;
  border-radius: 16px;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  border: 1px solid transparent;
  font-family: Inter;
  text-align: center;
}

.task-status-badge__date {
  color: #344054;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
}

/* To Do */
.task-status-badge--to-do {
  background-color: #f9fafb;
  color: #344054;
  border-color: #eaecf0;
}

/* In Progress */
.task-status-badge--in-progress {
  background-color: #eff8ff;
  color: #175cd3;
  border-color: #b2ddff;
}

/* Planned */
.task-status-badge--planned {
  background-color: #f9fafb;
  color: #344054;
  border-color: #eaecf0;
}

/* Completed */
.task-status-badge--completed {
  background-color: #ecfdf3;
  color: #067647;
  border-color: #abefc6;
}

/* Canceled — outlined, no fill (Figma update: 1.5px solid #475467 border) */
.task-status-badge--canceled {
  background-color: transparent;
  color: #344054;
  border: 1.5px solid #475467;
}
</style>
